import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { useLocation } from "wouter";
import {
  LayoutDashboard, Crosshair, Target, TrendingUp,
  X, ChevronRight, ChevronLeft, Rocket, Sparkles,
  Settings, CheckCircle2, Zap, Compass, Trophy, ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import logoUrl from "../assets/logo.png";

async function markOnboardingComplete() {
  const token = localStorage.getItem("token");
  await fetch("/api/user/onboarding", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ completed: true }),
  });
}

function Dots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5 my-4">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "rounded-full transition-all duration-300",
            i === current
              ? "w-6 h-2 bg-primary"
              : i < current
              ? "w-2 h-2 bg-primary/40"
              : "w-2 h-2 bg-border",
          )}
        />
      ))}
    </div>
  );
}

function NeverShowCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center justify-center gap-2 cursor-pointer select-none mt-3 group">
      <div
        onClick={() => onChange(!checked)}
        className={cn(
          "w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors",
          checked
            ? "bg-primary border-primary"
            : "bg-transparent border-border group-hover:border-muted-foreground",
        )}
      >
        {checked && (
          <svg className="w-2.5 h-2.5 text-primary-foreground" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span
        onClick={() => onChange(!checked)}
        className="text-xs text-muted-foreground group-hover:text-foreground transition-colors"
      >
        {label}
      </span>
    </label>
  );
}

export function OnboardingTour() {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const [location, navigate] = useLocation();
  const [step, setStep] = useState<number>(-1);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (location === "/settings") return;
    if (user.onboardingCompleted) return;
    const id = setTimeout(() => setStep(0), 900);
    return () => clearTimeout(id);
  }, [user, location]);

  const persistIfChecked = () => {
    if (dontShow) {
      markOnboardingComplete().then(() => updateUser({ onboardingCompleted: true })).catch(() => {});
    }
  };

  const closeTour = () => {
    persistIfChecked();
    setStep(-1);
  };

  const goToPractice = () => {
    persistIfChecked();
    setStep(-1);
    navigate("/practice");
  };

  const goToSubscription = () => {
    persistIfChecked();
    setStep(-1);
    navigate("/subscription?from=onboarding");
  };

  if (step < 0 || !user) return null;

  const o = t.onboarding;
  const firstName = (user.fullName ?? "Pilot").split(" ")[0];

  const featureSteps = [
    {
      Icon: LayoutDashboard,
      color: "text-sky-500",
      bg: "bg-sky-500/10",
      title: o.step1Title,
      body: o.step1Body,
    },
    {
      Icon: Crosshair,
      color: "text-primary",
      bg: "bg-primary/10",
      title: o.step2Title,
      body: o.step2Body,
    },
    {
      Icon: Sparkles,
      color: "text-purple-500",
      bg: "bg-purple-500/10",
      title: o.step3Title,
      body: o.step3Body,
      badge: o.step3Badge,
    },
    {
      Icon: Target,
      color: "text-orange-400",
      bg: "bg-orange-400/10",
      title: o.step4Title,
      body: o.step4Body,
    },
    {
      Icon: TrendingUp,
      color: "text-amber-500",
      bg: "bg-amber-500/10",
      title: o.step5Title,
      body: o.step5Body,
    },
    {
      Icon: Settings,
      color: "text-teal-500",
      bg: "bg-teal-500/10",
      title: o.step6Title,
      body: o.step6Body,
    },
    {
      Icon: Compass,
      color: "text-cyan-500",
      bg: "bg-cyan-500/10",
      title: o.step7Title,
      body: o.step7Body,
    },
    {
      Icon: Trophy,
      color: "text-yellow-500",
      bg: "bg-yellow-500/10",
      title: o.step8Title,
      body: o.step8Body,
    },
    {
      Icon: ShieldCheck,
      color: "text-emerald-500",
      bg: "bg-emerald-500/10",
      title: o.step9Title,
      body: o.step9Body,
    },
  ];

  const TOTAL = featureSteps.length + 1;
  const upsellBullets = [o.upsell1, o.upsell2, o.upsell3, o.upsell4, o.upsell5];
  const FEATURE_LAST = featureSteps.length;
  const FINAL_STEP = FEATURE_LAST + 1;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">

      {/* ── Step 0: Welcome ─────────────────────────────────────── */}
      {step === 0 && (
        <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <button onClick={closeTour} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>

          <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-8 pt-10 pb-5 text-center">
            <img src={logoUrl} alt="Flight Deck" className="h-10 w-auto mx-auto mb-5 drop-shadow-lg" />
            <h2 className="text-2xl font-bold text-foreground">
              {o.welcomeTitle.replace("{name}", firstName)}
            </h2>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed max-w-sm mx-auto">
              {o.welcomeBody}
            </p>
          </div>

          <div className="px-8 pb-8">
            <Dots total={TOTAL} current={0} />
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={closeTour} className="flex-1 px-4 py-2.5 rounded-lg border border-border text-muted-foreground text-sm font-medium hover:bg-secondary transition-colors">
                {o.skip}
              </button>
              <button onClick={() => setStep(1)} className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                {o.letsGo} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <NeverShowCheckbox checked={dontShow} onChange={setDontShow} label={o.neverShow} />
          </div>
        </div>
      )}

      {/* ── Steps 1–N: Feature highlights ───────────────────────── */}
      {step >= 1 && step <= FEATURE_LAST && (() => {
        const { Icon, color, bg, title, body, badge } = featureSteps[step - 1];
        const isLast = step === FEATURE_LAST;
        return (
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button onClick={closeTour} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary transition-colors">
              <X className="w-4 h-4" />
            </button>

            <div className="px-8 pt-10 pb-0 text-center">
              <div className="relative inline-flex mx-auto mb-4">
                <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center", bg)}>
                  <Icon className={cn("w-7 h-7", color)} />
                </div>
                {badge && (
                  <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {badge}
                  </span>
                )}
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">{body}</p>
              <Dots total={TOTAL} current={step} />
            </div>

            <div className="px-8 pb-8">
              <div className="flex gap-3">
                <button onClick={() => setStep(step - 1)} className="px-4 py-2.5 rounded-lg border border-border text-muted-foreground text-sm font-medium hover:bg-secondary transition-colors flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> {o.back}
                </button>
                <button onClick={() => setStep(step + 1)} className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                  {isLast ? o.finish : o.next} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <NeverShowCheckbox checked={dontShow} onChange={setDontShow} label={o.neverShow} />
            </div>
          </div>
        );
      })()}

      {/* ── Final: Upsell + CTA ──────────────────────────────────── */}
      {step === FINAL_STEP && (
        <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <button onClick={closeTour} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>

          <div className="bg-gradient-to-br from-primary/20 via-primary/5 to-transparent px-8 pt-8 pb-5 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-3">
              <Rocket className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-xl font-black text-foreground">{o.finalTitle}</h2>
            <p className="text-muted-foreground text-sm mt-1 leading-snug">{o.finalSubtitle}</p>
          </div>

          <div className="px-8 py-4 border-t border-border/50">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-xs font-bold text-foreground tracking-wide uppercase">{o.finalWhatsIncluded}</span>
            </div>
            <ul className="space-y-2">
              {upsellBullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="px-8 pb-8 pt-4 flex flex-col gap-2.5">
            <button
              onClick={goToSubscription}
              className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-black hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4" /> {o.upgradeNow}
            </button>
            <button
              onClick={goToPractice}
              className="w-full px-4 py-2.5 rounded-lg border border-primary/30 text-primary text-sm font-bold hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
            >
              <Crosshair className="w-4 h-4" /> {o.startPracticing}
            </button>
            <button
              onClick={closeTour}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              {o.goToDashboard}
            </button>
            <NeverShowCheckbox checked={dontShow} onChange={setDontShow} label={o.neverShow} />
          </div>
        </div>
      )}
    </div>
  );
}
